/**
 * Générateur — Automatisme : utiliser les théorèmes de la droite des milieux
 * (pour prouver que des droites sont parallèles, pour calculer une longueur,
 * pour prouver qu'un point est le milieu d'un côté).
 * Attendu ID : 1244 | Niveau : 3ème | Domaine : Espace et géométrie, Triangles
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 l. 1097-1101 ; reprend
 * les trois théorèmes de l'objectif 4e l. 1040-1042 sans gen en base).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * GEN NEUF, trois atomes = trois cartes, k = 2 seulement, JAMAIS Thalès
 * (objectif l. 1104 ; MathsMentales 4GB9/4GB10 refusés). Figure codée
 * (figTriangle.js, ticks d'égalité comme 1078), sommet porteur et orientation
 * variés, mêmes lettres saines que 1243 (triple {A, B, C} sacrifié en 1243,
 * pas ici). Longueurs : BC pair 4..12 (BC impair À ARBITRER).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Parallèle » (parallele_qcm_unique) : M milieu de [KL], N milieu
 *     de [KT] codés ; « (MN) est parallèle à … » [(LT) ; (KL) ; (KT)].
 *   Carte 2 « Longueur » (longueur_flash_texte_trou_saisie) : « LT = 8 cm,
 *     donc MN = ⬚ cm » ou « MN = 3 cm, donc LT = ⬚ cm », deux lignes.
 *   Carte 3 « Milieu » (milieu_qcm_unique) : M milieu de [KL] codé, (MN) ∥
 *     (LT) dessinée ; « N est le milieu de … » [[KT] ; [LT] ; [KL]].
 *   Carte 4 « Vrai ou faux » (sens_tableau_vf) : parallèle, longueur, milieu,
 *     hypothèse manquante.
 *   Carte 5 « Le raisonnement » (redaction_texte_trou_drag) : la rédaction à
 *     compléter avec des étiquettes, sens direct ou réciproque (retour prof
 *     13/09 : un raisonnement guidé, sans Thalès ni papillon).
 *
 * INVARIANTS : le sommet porteur (celui dont partent les deux côtés coupés
 * aux milieux) est tiré parmi les trois ; la figure porte data-figure
 * (points K, L, T, M, N) et les codages (ticks) ; distracteurs distincts.
 * SACRIFIÉ (fiche « la moitié ») : (LT = 8 ; MN = 4), jamais tiré dans les
 * deux sens ; auto-testé.
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   mauvais_cote                  : (MN) ∥ (KL) (un côté coupé pris pour le troisième), PRINCIPALE ;
 *   cote_recopie                  : MN = 8 pour LT = 8 ;
 *   sens_inverse                  : MN = 16 (le double au lieu de la moitié) ;
 *   hypothese_manquante           : conclure sans le second milieu ou sans le parallélisme ;
 *   perpendiculaire_pour_parallele : (MN) ⟂ (LT).
 */
import {
    randomChoice, randInt, shuffle, pickLettresSains, tex,
    makeQcm, makeVraiFaux, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { svgTriangle, tourner, milieu } from './figTriangle';

const ATTENDU_ID = 1244;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PARALLELE = 'Parallèle';
const L_LONGUEUR = 'Longueur';
const L_MILIEU = 'Milieu';
const L_VF = 'Vrai ou faux';
const L_REDACTION = 'Le raisonnement';

const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');
/** Le couple sacrifié (fiche) : le troisième côté 8, le segment des milieux 4. */
export const SACRIFIE = { cote: 8, moitie: 4 };

const EXPLICATIONS = {
    regle: 'Dans un triangle, la droite qui passe par les milieux de deux côtés est parallèle au troisième côté, et le segment des milieux mesure la moitié de ce côté.',
    mauvais_cote: 'La droite des milieux est parallèle au côté qu\'elle ne touche pas : le troisième côté.',
    cote_recopie: 'Le segment des milieux mesure la MOITIÉ du troisième côté, pas la même longueur.',
    sens_inverse: 'Le segment des milieux est le plus court : la moitié du troisième côté ; le troisième côté est le double.',
    hypothese_manquante: 'Il faut deux milieux pour la parallèle et la longueur ; pour prouver un milieu, il faut un milieu ET la parallèle.',
    perpendiculaire_pour_parallele: 'La droite des milieux est PARALLÈLE au troisième côté.',
};

/**
 * Un triangle nommé (lettres saines) avec le sommet porteur P, ses voisins U et V, M milieu de [PU],
 * N milieu de [PV] ; le troisième côté est [UV]. Coordonnées abstraites tournées.
 */
const tirer = () => {
    const [P, U, V, M, N] = pickLettresSains(POOL, 5);
    const base = [[0, 0], [randInt(6, 9), 0], [randomChoice([-2, -1, 1, 2, 3]), randInt(4, 7)]];   // jamais rectangle en P (x ≠ 0)
    const pts = tourner(base, randomChoice([0, 40, 85, 130, 175, 220, 265, 310]));
    const points = { [P]: pts[0], [U]: pts[1], [V]: pts[2], [M]: milieu(pts[0], pts[1]), [N]: milieu(pts[0], pts[2]) };
    return { P, U, V, M, N, points, nom: `${P}${U}${V}` };
};
const droite = (a, b) => tex(`(${a}${b})`);
const segment = (a, b) => tex(`[${a}${b}]`);
const scene = (t, { ticks = true, mn = true, tickN = true } = {}) => ({
    points: t.points,
    triangle: [t.P, t.U, t.V],
    segments: [[t.P, t.U], [t.U, t.V], [t.V, t.P]],
    pointilles: mn ? [[t.M, t.N]] : [],
    ticks: ticks ? [{ de: t.P, a: t.M, n: 1 }, { de: t.M, a: t.U, n: 1 }, ...(tickN ? [{ de: t.P, a: t.N, n: 2 }, { de: t.N, a: t.V, n: 2 }] : [])] : [],
});
const figure = (t, o) => imgElement(svgTriangle(scene(t, o), `Le triangle ${t.nom} ; ${t.M} sur [${t.P}${t.U}], ${t.N} sur [${t.P}${t.V}], le segment [${t.M}${t.N}] en pointillés.`), 'large');

// ─── Carte 1 : parallèle (QCM 3 choix) ────────────────────────────────────

const genParallele = () => {
    const t = tirer();
    const bon = droite(t.U, t.V);
    const faux = shuffle([{ v: droite(t.P, t.U), code: 'mauvais_cote' }, { v: droite(t.P, t.V), code: 'mauvais_cote' }]);
    const texte = `<p>Dans le triangle ${tex(t.nom)}, ${tex(t.M)} est le milieu de ${segment(t.P, t.U)} et ${tex(t.N)} est le milieu de ${segment(t.P, t.V)}.</p><p><b>La droite ${droite(t.M, t.N)} est parallèle à…</b></p>`;
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, texte, { ...opts(L_PARALLELE), elements_additionnels: figure(t) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...t, points: undefined, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 2 : longueur (flash, deux lignes) ──────────────────────────────

const tirerCote = () => { for (;;) { const c = 2 * randInt(2, 6); if (c !== SACRIFIE.cote) return c; } };
const tirerMoitie = () => { for (;;) { const m = randInt(2, 6); if (m !== SACRIFIE.moitie) return m; } };

const genLongueur = () => {
    const t = tirer();
    const direct = randInt(0, 1) === 1;
    const c = tirerCote(); const m = tirerMoitie();
    const l1 = direct
        ? `${tex(`${t.U}${t.V} = ${c}`)} cm, donc ${tex(`${t.M}${t.N}`)} = {{${c / 2}}} cm.`
        : `${tex(`${t.M}${t.N} = ${m}`)} cm, donc ${tex(`${t.U}${t.V}`)} = {{${2 * m}}} cm.`;
    const l2 = direct
        ? `${tex(`${t.M}${t.N} = ${m}`)} cm, donc ${tex(`${t.U}${t.V}`)} = {{${2 * m}}} cm.`
        : `${tex(`${t.U}${t.V} = ${c}`)} cm, donc ${tex(`${t.M}${t.N}`)} = {{${c / 2}}} cm.`;
    const texte = `<p>Dans le triangle ${tex(t.nom)}, ${tex(t.M)} est le milieu de ${segment(t.P, t.U)} et ${tex(t.N)} est le milieu de ${segment(t.P, t.V)}.</p><p><b>Complète.</b></p>`;
    const payload = makeTexteTrou(`${l1}\n${l2}`, texte, { ...opts(L_LONGUEUR), mode: 'saisie', strict: false, elements_additionnels: figure(t) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...t, points: undefined, cote: c, moitie: m, direct };
    return payload;
};

// ─── Carte 3 : milieu (QCM 3 choix) ───────────────────────────────────────

const genMilieu = () => {
    const t = tirer();
    const bon = segment(t.P, t.V);
    const faux = shuffle([{ v: segment(t.U, t.V), code: 'mauvais_cote' }, { v: segment(t.P, t.U), code: 'mauvais_cote' }]);
    const texte = `<p>Dans le triangle ${tex(t.nom)}, ${tex(t.M)} est le milieu de ${segment(t.P, t.U)} et la droite ${droite(t.M, t.N)} est parallèle à ${droite(t.U, t.V)}.</p><p><b>${tex(t.N)} est le milieu de…</b></p>`;
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, texte, { ...opts(L_MILIEU), elements_additionnels: figure(t, { tickN: false }) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...t, points: undefined, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 4 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    const t = tirer();
    const c = tirerCote();
    const candidats = [
        { l: `${droite(t.M, t.N)} est parallèle à ${droite(t.U, t.V)}.`, v: true, fam: 'parallele' },
        { l: `${droite(t.M, t.N)} est parallèle à ${droite(t.P, randomChoice([t.U, t.V]))}.`, v: false, fam: 'parallele', code: 'mauvais_cote' },
        { l: `${droite(t.M, t.N)} est perpendiculaire à ${droite(t.U, t.V)}.`, v: false, fam: 'parallele', code: 'perpendiculaire_pour_parallele' },
        { l: `Si ${tex(`${t.U}${t.V} = ${c}`)} cm, alors ${tex(`${t.M}${t.N} = ${c / 2}`)} cm.`, v: true, fam: 'longueur' },
        { l: `Si ${tex(`${t.U}${t.V} = ${c}`)} cm, alors ${tex(`${t.M}${t.N} = ${c}`)} cm.`, v: false, fam: 'longueur', code: 'cote_recopie' },
        { l: `Si ${tex(`${t.U}${t.V} = ${c}`)} cm, alors ${tex(`${t.M}${t.N} = ${2 * c}`)} cm.`, v: false, fam: 'longueur', code: 'sens_inverse' },
        { l: `${tex(`${t.M}${t.N}`)} est la moitié de ${tex(`${t.U}${t.V}`)}.`, v: true, fam: 'moitie' },
        { l: `${tex(`${t.U}${t.V}`)} est la moitié de ${tex(`${t.M}${t.N}`)}.`, v: false, fam: 'moitie', code: 'sens_inverse' },
        { l: `Pour prouver que ${tex(t.N)} est le milieu de ${segment(t.P, t.V)}, il suffit de savoir que ${tex(t.M)} est le milieu de ${segment(t.P, t.U)}.`, v: false, fam: 'hypothese', code: 'hypothese_manquante' },
        { l: `Pour prouver que ${tex(t.N)} est le milieu de ${segment(t.P, t.V)}, il faut savoir que ${tex(t.M)} est le milieu de ${segment(t.P, t.U)} et que ${droite(t.M, t.N)} est parallèle à ${droite(t.U, t.V)}.`, v: true, fam: 'hypothese' },
    ];
    for (let essai = 0; essai < 100; essai++) {
        const nbFaux = randInt(1, 3);
        const fausses = shuffle(candidats.filter((x) => !x.v)); const vraies = shuffle(candidats.filter((x) => x.v));
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((x) => x.fam)).size !== 4) continue;
        const texte = `<p>Dans le triangle ${tex(t.nom)}, ${tex(t.M)} est le milieu de ${segment(t.P, t.U)} et ${tex(t.N)} est le milieu de ${segment(t.P, t.V)}.</p><p><b>Vrai ou faux ?</b></p>`;
        const payload = makeVraiFaux(lignes.map((x) => x.l), lignes.map((x) => (x.v ? 0 : 1)), texte, { ...opts(L_VF), elements_additionnels: figure(t) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...t, points: undefined, cote: c, distracteurs: lignes.filter((x) => x.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_1244 : V/F sans tirage');
};

// ─── Carte 5 : le raisonnement guidé (glisser-déposer) ────────────────────

/**
 * Deux rédactions (retour prof 13/09) : le sens direct « D'après le théorème
 * des milieux, (MN) est parallèle à (UV) et MN = UV ÷ 2 » ; la réciproque
 * « M milieu de [PU] et (MN) ∥ (UV), donc N est le milieu de [PV] ». Les
 * tuiles : le nom du théorème (milieux, Pythagore, Thalès), les droites, les
 * côtés, les segments ; trous hors du mode équation, tuiles en KaTeX.
 */
const genRedaction = () => {
    for (let essai = 0; essai < 100; essai++) {
        const t = tirer();
        const direct = randInt(0, 1) === 1;
        const nomTheoreme = 'des milieux';
        // dans les trous et les tuiles : lettres dans l'ordre alphabétique et KaTeX NU « $(KM)$ » (pas de \mathrm{} :
        // des accolades dans un trou cassent le parseur des textes à trous)
        const tri = (a, b) => [a, b].sort().join('');
        const nu = (x) => `$${x}$`;
        let phrase; let tuiles;
        if (direct) {
            phrase = `${tex(t.M)} est le milieu de ${segment(t.P, t.U)} et ${tex(t.N)} est le milieu de ${segment(t.P, t.V)}.\n`
                + `D'après le théorème {{${nomTheoreme}}}, ${droite(t.M, t.N)} est parallèle à {{${nu(`(${tri(t.U, t.V)})`)}}} et ${tex(`${t.M}${t.N}`)} = {{${nu(tri(t.U, t.V))}}} ÷ 2.`;
            tuiles = ['de Pythagore', 'de Thalès', nu(`(${tri(t.P, t.U)})`), nu(`(${tri(t.P, t.V)})`), nu(tri(t.P, t.U)), nu(tri(t.M, t.N))];
        } else {
            phrase = `${tex(t.M)} est le milieu de ${segment(t.P, t.U)} et ${droite(t.M, t.N)} est parallèle à ${droite(t.U, t.V)}.\n`
                + `D'après le théorème {{${nomTheoreme}}}, ${tex(t.N)} est le milieu de {{${nu(`[${tri(t.P, t.V)}]`)}}}.`;
            tuiles = ['de Pythagore', 'de Thalès', nu(`[${tri(t.U, t.V)}]`), nu(`[${tri(t.P, t.U)}]`)];
        }
        const leurres = [...new Set(filtrerDistracteurs(phrase, tuiles))];
        if (leurres.length < 3) continue;
        const payload = makeTexteTrou(phrase, `<p>Dans le triangle ${tex(t.nom)}.</p><p><b>Complète la rédaction avec les étiquettes.</b></p>`, { ...opts(L_REDACTION), mode: 'drag', distracteurs: leurres, elements_additionnels: figure(t, { tickN: direct }) });
        payload.contenu = { ...payload.contenu, sousType: direct ? 'direct' : 'reciproque', explications: EXPLICATIONS };
        payload._meta.params = { ...t, points: undefined, direct, distracteurs: leurres.map(() => ({ code: 'mauvais_cote' })) };
        return payload;
    }
    throw new Error('gen_1244 : rédaction sans tirage');
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'k = 2 seulement, jamais Thalès ; sommet porteur et orientation variés ; lettres saines ; figure codée (ticks) avec le segment des milieux en pointillés',
        'sacrifié (troisième côté 8 ; segment des milieux 4) dans les deux sens',
    ],
    parallele_qcm_unique: 'mauvais_cote : les deux côtés coupés (trois choix)',
    milieu_qcm_unique: 'mauvais_cote : le troisième côté et le côté déjà coupé (trois choix)',
    sens_tableau_vf: 'quatre familles exclusives : parallèle (mauvais côté, perpendiculaire), longueur (recopie, double), la moitié (sens inverse), hypothèse manquante',
    redaction_texte_trou_drag: 'le nom du théorème (Pythagore, Thalès en leurres), le côté parallèle et le côté doublé (mauvais_cote), ou le segment dont N est le milieu',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    parallele_qcm_unique: genParallele,
    longueur_flash_texte_trou_saisie: genLongueur,
    milieu_qcm_unique: genMilieu,
    sens_tableau_vf: genVf,
    redaction_texte_trou_drag: genRedaction,
};

export const CATEGORIES = [
    { id: 'parallele', label: L_PARALLELE, formatsList: [{ key: 'parallele_qcm_unique', label: 'QCM' }] },
    { id: 'longueur', label: L_LONGUEUR, formatsList: [{ key: 'longueur_flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'milieu', label: L_MILIEU, formatsList: [{ key: 'milieu_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
    { id: 'redaction', label: L_REDACTION, formatsList: [{ key: 'redaction_texte_trou_drag', label: 'Glisser-déposer' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
